import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import "c3/c3.min.css";
import * as d3 from "d3";
import PromptDialog, {
  InputTextItem,
} from "../../../../../common/PromptDialog";
import { ITrimcopyFileRequestModel } from "../../../../../webapi/service/trimcopyFile";
import {
  C3ChartID,
  SVGSelecter,
  getRootElement,
  insertRangeElement,
  getRangeElement,
  displayChartPoint,
  getAxisXValues,
  startRangeSelection,
  setRangeSelection,
  insertZoomTarget,
  AxisRange,
  setMousewheel,
  setXAxisRange,
  setYAxisRange,
  setZoomTarget,
  transpose,
  ChartSize,
  resizeChart,
} from "../common/chart";
import ChartDialog from "../common/ChartDialog";
import { IProtData } from "../../../../../webapi/service/getFIle";
import { AnyType, ChartDialogConfig } from "../../../../../common/constants";
import "../../../../../App.css";

/**
 * C3に設定するY軸範囲を取得します
 * @param rows - チャートデータ
 * @returns Y軸範囲
 */
function getYAxisRange(rows: C3.PrimitiveArray[]): AxisRange {
  const find = rows.find((row: AnyType) => {
    return row[0] === "x";
  });
  const filter = find
    ? transpose(rows).filter((row) => {
        return row[0] !== "x";
      })
    : rows;
  const values = filter.flat().filter((val) => {
    return typeof val === "number";
  });
  const min = values.reduce((v1, v2) => {
    return Math.min(v1 as number, v2 as number);
  }) as number;
  const max = values.reduce((v1, v2) => {
    return Math.max(v1 as number, v2 as number);
  }) as number;
  return { min: min, max: max };
}

export type TrimmingChartProt = IProtData | undefined;

/**
 * マウスドラッグ情報
 */
type DragInfo = {
  /**
   * ドラッグ中の場合trueを設定します
   */
  drag: boolean;
};

/**
 * マウスクリック位置。
 * Zoomターゲットに使用
 */
type ClickPos = {
  /**
   * X座標
   */
  x: number;
  /**
   * Y座標
   */
  y: number;
};

export interface IProps extends WithTranslation {
  /**
   * チャートダイアログを表示する場合はtrue
   */
  show: boolean;
  /**
   * チャート幅
   */
  width: number;
  /**
   * チャート高さ
   */
  height: number;
  edit: boolean;
  /**
   * チャートデータ
   */
  prot: TrimmingChartProt;
  /**
   * トリミングデータを保存する場合に呼び出します
   */
  onOk: (trimcopy: ITrimcopyFileRequestModel) => void;
  /**
   * チャートダイアログを閉じる場合に呼び出します
   */
  onCancel: () => void;
}

interface IState {
  /**
   * 編集モードにする場合はtrue
   */
  edit: boolean;
  /**
   * ファイル名入力ダイアログを表示する場合はtrue
   */
  showFileSave: boolean;
}

class TrimmingChartDialog extends React.Component<IProps, IState> {
  private readonly ChartID = "chart_trimming";
  private readonly C3ChartID = C3ChartID(this.ChartID);
  private readonly SvcTag = SVGSelecter(this.C3ChartID);
  private _c3: C3.ChartAPI | null = null;
  private _chartSize: ChartSize = { width: 0, height: 0 };
  private _dragInfo: DragInfo = { drag: false };
  private _contextmenuEnabled = true;
  private readonly XScaleRate = ChartDialogConfig.CHART_X_AXIS_SCALE_RATE;
  private _xZoomCount = 0;
  private _xAxisRange: AxisRange = { min: 0, max: 0 };
  private readonly YScaleRate = ChartDialogConfig.CHART_Y_AXIS_SCALE_RATE;
  private _yZoomCount = 0;
  private _yAxisRange: AxisRange = { min: 0, max: 0 };
  private _clickPos: ClickPos = { x: 0, y: 0 };

  constructor(props: IProps) {
    super(props);
    this.state = {
      edit: false,
      showFileSave: false,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleXAxisZoomUp = this.handleXAxisZoomUp.bind(this);
    this.handleXAxisZoomOut = this.handleXAxisZoomOut.bind(this);
    this.handleYAxisZoomUp = this.handleYAxisZoomUp.bind(this);
    this.handleYAxisZoomOut = this.handleYAxisZoomOut.bind(this);
    this.handleCloseFileSave = this.handleCloseFileSave.bind(this);
    this.handleSaveFile = this.handleSaveFile.bind(this);
    this.handleChartMouseup = this.handleChartMouseup.bind(this);
    this.dragInfo = this.dragInfo.bind(this);
    this.clickStart = this.clickStart.bind(this);
    this.chartSize = this.chartSize.bind(this);
  }

  componentDidUpdate(prevProps: IProps) {
    const { prot, width, height } = this.props;
    if (!prevProps.prot && prot) {
      console.time(prot.fname);
      this.drawChart(
        prot.data,
        this.C3ChartID,
        { width: width, height: height },
        prot.datanum
      );
      console.timeEnd(prot.fname);
      this._contextmenuEnabled = true;
      this._xZoomCount = 0;
      this._yZoomCount = 0;
    }
  }

  /**
   * ダイアログクローズイベント
   */
  handleClose() {
    this.setState({ edit: false });
    this.props.onCancel();
  }

  /**
   * 編集ボタンイベント
   */
  handleEdit() {
    if (this._c3) {
      this._contextmenuEnabled = false;
      displayChartPoint(this._c3, true);
      this.editChart(this._c3, true);
      this.setState({ edit: true });
    }
  }

  /**
   * 保存ボタンイベント
   */
  handleSave() {
    if (this._c3) {
      const range = getRangeElement(this._c3);
      const start = parseInt(range.attr("start"));
      const end = parseInt(range.attr("end"));
      if (end - start > 0) {
        this.setState({ showFileSave: true });
      } else {
        displayChartPoint(this._c3, false);
        this.setState({ edit: false });
      }
    }
  }

  /**
   * X軸制限値プロパティ
   * @param range - X軸範囲
   * @returns X軸範囲
   */
  xAxisRange(range?: AxisRange): AxisRange {
    if (range) {
      this._xAxisRange = range;
    }
    return this._xAxisRange;
  }

  /**
   * Y軸制限値プロパティ
   * @param range - Y軸範囲
   * @returns Y軸範囲
   */
  yAxisRange(range?: AxisRange): AxisRange {
    if (range) {
      this._yAxisRange = range;
    }
    return this._yAxisRange;
  }

  /**
   * X軸拡大回数プロパティ
   * @param count - X軸拡大回数
   * @returns X軸拡大回数
   */
  xZoomCount(count?: number): number {
    if (typeof count !== "undefined") {
      this._xZoomCount = count;
    }
    return this._xZoomCount;
  }

  /**
   * Y軸拡大回数プロパティ
   * @param count - Y軸拡大回数
   * @returns Y軸拡大回数
   */
  yZoomCount(count?: number): number {
    if (typeof count !== "undefined") {
      this._yZoomCount = count;
    }
    return this._yZoomCount;
  }

  /**
   * X軸拡大率を取得します
   * @param count - X軸拡大回数
   * @returns X軸拡大率
   */
  getXRate(count: number): number {
    return count * this.XScaleRate;
  }

  /**
   * Y軸拡大率を取得します
   * @param count - Y軸拡大回数
   * @returns Y軸拡大率
   */
  getYRate(count: number): number {
    return count * this.YScaleRate;
  }

  /**
   * X軸拡大ボタンイベント
   */
  handleXAxisZoomUp() {
    const c3 = this._c3;
    if (c3) {
      const range = this.xAxisRange();
      const count = this.xZoomCount() + 1;
      const rate = this.getXRate(count);
      if (rate <= 1) {
        setXAxisRange(c3, rate >= 1 ? 0.95 : rate, range);
        this.xZoomCount(count);
      }
    }
  }

  /**
   * X軸縮小ボタンイベント
   */
  handleXAxisZoomOut() {
    const c3 = this._c3;
    if (c3) {
      const range = this.xAxisRange();
      const count = this.xZoomCount() - 1;
      const rate = this.getXRate(count);
      if (rate >= 0) {
        setXAxisRange(c3, rate, range);
        this.xZoomCount(count);
      }
    }
  }

  /**
   * Y軸拡大ボタンイベント
   */
  handleYAxisZoomUp() {
    const c3 = this._c3;
    if (c3) {
      const range = this.yAxisRange();
      const count = this.yZoomCount() + 1;
      const rate = this.getYRate(count);
      if (rate <= 1) {
        setYAxisRange(c3, rate >= 1 ? 0.95 : rate, range);
        this.yZoomCount(count);
      }
    }
  }

  /**
   * Y軸縮小ボタンイベント
   */
  handleYAxisZoomOut() {
    const c3 = this._c3;
    if (c3) {
      const range = this.yAxisRange();
      const count = this.yZoomCount() - 1;
      const rate = this.getYRate(count);
      if (rate >= 0) {
        setYAxisRange(c3, rate, range);
        this.yZoomCount(count);
      }
    }
  }

  /**
   * ファイル名入力ダイアログクローズボタンイベント
   */
  handleCloseFileSave() {
    this.setState({ showFileSave: false });
  }

  /**
   * ファイル名入力ダイアログ保存ボタンイベント
   * @param texts - 入力ダイアログアイテム
   */
  handleSaveFile(texts: InputTextItem[]) {
    if (this._c3) {
      const range = getRangeElement(this._c3);
      const name: string = texts[0].text;
      this.setState({ edit: false, showFileSave: false });
      this.props.onOk({
        name: name,
        trimstart: parseInt(range.attr("start")) - 1,
        trimend: parseInt(range.attr("end")) - 1,
      });
    }
  }

  /**
   * マウスドラッグプロパティ
   * @param dragInfo - マウスドラッグ情報
   * @returns マウスドラッグ情報
   */
  dragInfo(dragInfo?: DragInfo) {
    if (dragInfo) {
      this._dragInfo = dragInfo;
    }
    return this._dragInfo;
  }

  /**
   * マウスクリック位置プロパティ
   * @param clickPos - マウスクリック位置
   * @returns マウスクリック位置
   */
  clickStart(clickPos?: ClickPos) {
    if (clickPos) {
      this._clickPos = clickPos;
    }
    return this._clickPos;
  }

  /**
   * マウスUPイベント
   */
  handleChartMouseup() {
    this.dragInfo({ drag: false });
    window.removeEventListener("mouseup", this.handleChartMouseup);
  }

  /**
   * コンテキストメニューイベント追加
   * @param selecter コンテキストメニューイベント追加位置
   */
  addContextMenu(selecter: string): void {
    const element = document.querySelector(selecter);
    if (element) {
      element.addEventListener("contextmenu", (e) => {
        if (this._contextmenuEnabled === true) {
          return true;
        } else {
          e.preventDefault();
        }
      });
    }
  }

  /**
   * C3チャートを生成します
   * @param rows - チャートデータ
   * @param chartid - チャートID
   * @param size - チャートサイズ
   * @param datanum - X軸データ点数
   * @param rangeY - Y軸範囲
   */
  generateChart(
    rows: C3.PrimitiveArray[],
    chartid: string,
    size: ChartSize,
    datanum: number,
    rangeY: AxisRange
  ) {
    this._c3 = C3.generate({
      bindto: chartid,
      size: {
        width: size.width,
        height: size.height,
      },
      data: { x: "x", rows: rows, type: "line" },
      axis: {
        x: { min: 1, max: datanum },
        y: { min: rangeY.min, max: rangeY.max },
      },
      point: {
        show: true,
      },
    });
    insertRangeElement(this._c3, size.height);
    insertZoomTarget(this._c3);
  }

  /**
   * チャートサイズプロパティ
   * @param size - チャートサイズ
   * @returns チャートサイズ
   */
  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  /**
   * チャートX軸Y軸を初期化します
   * @param c3 - C3
   */
  initAxisRange(c3: C3.ChartAPI) {
    const { min, max } = c3.axis.range();
    this.xAxisRange({ min: min.x, max: max.x });
    this.yAxisRange({ min: min.y, max: max.y });
  }

  /**
   * チャートを描画します
   * @param rows - チャートデータ
   * @param chartid - チャートID
   * @param size - チャートサイズ
   * @param datanum - X軸データ点数
   */
  drawChart(
    rows: C3.PrimitiveArray[],
    chartid: string,
    size: ChartSize,
    datanum: number
  ) {
    const rangeY = getYAxisRange(rows);
    if (!this._c3) {
      this.generateChart(rows, chartid, size, datanum, rangeY);
      this.addContextMenu(this.SvcTag);
    } else {
      this._c3.destroy();
      this.generateChart(rows, chartid, size, datanum, rangeY);
    }
    this.chartSize(size);
    if (this._c3) {
      this.initAxisRange(this._c3);
      displayChartPoint(this._c3, false);
      this.editChart(this._c3, false);
      setMousewheel(this._c3, this.xAxisRange(), datanum);
    }
  }

  /**
   * チャートの編集可不可状態を切り替えます
   * @param c3 - C3
   * @param edit - 編集可にする場合はtrue
   */
  editChart(c3: C3.ChartAPI, edit: boolean) {
    const c3i = c3.internal;
    const root = getRootElement(c3);
    if (edit) {
      const margin = c3i.margin;
      const xs = getAxisXValues(c3);
      const onmouseup = this.handleChartMouseup;
      const dragInfo = this.dragInfo;
      const clickStart = this.clickStart;
      root
        .on("mousedown", function () {
          const event = d3.event;
          let mouseX = event.offsetX - margin.left;
          if (mouseX < 0) {
            mouseX = 0;
          }
          const mouseY = event.offsetY - margin.top;
          startRangeSelection(c3, xs, mouseX);
          dragInfo({ drag: true });
          clickStart({ x: mouseX, y: mouseY });
          window.addEventListener("mouseup", onmouseup);
        })
        .on("mousemove", function () {
          const { drag } = dragInfo();
          if (!drag) {
            return;
          }
          const event = d3.event;
          let mouseX = event.offsetX - margin.left;
          if (mouseX < 0) {
            mouseX = 0;
          }
          setRangeSelection(c3, xs, mouseX);
        })
        .on("mouseup", function () {
          const { x, y } = clickStart();
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          if (x === mouseX && y === mouseY) {
            setZoomTarget(c3, mouseX, mouseY);
          }
        })
        .on("click", null);
    } else {
      const margin = c3i.margin;
      root
        .on("mousedown", null)
        .on("mousemove", null)
        .on("mouseup", null)
        .on("click", function () {
          const event = d3.event;
          const mouseX = event.offsetX - margin.left;
          const mouseY = event.offsetY - margin.top;
          setZoomTarget(c3, mouseX, mouseY);
        });
    }
  }

  render() {
    const { show, width, height, t } = this.props;
    if (this._c3) {
      resizeChart(this._c3, width, height, this.chartSize);
    }
    return (
      <div>
        <ChartDialog
          open={show}
          chartid={this.ChartID}
          edit={this.state.edit}
          editMenu={false}
          onEdit={this.handleEdit}
          onSave={this.handleSave}
          onXZoomUp={this.handleXAxisZoomUp}
          onXZoomOut={this.handleXAxisZoomOut}
          onYZoomUp={this.handleYAxisZoomUp}
          onYZoomOut={this.handleYAxisZoomOut}
          onClose={this.handleClose}
        />
        <PromptDialog
          show={this.state.showFileSave}
          title={t("project_detail_editfile_save")}
          items={[
            {
              id: "fname",
              label: t("project_detail_editfile_filename"),
              placeholder: t("project_detail_editfile_inputfilename"),
              validate: { required: true },
            },
          ]}
          onOk={this.handleSaveFile}
          onCancel={this.handleCloseFileSave}
        />
      </div>
    );
  }
}

/**
 * トリミングダイアログ
 */
export default withTranslation()(TrimmingChartDialog);
